import toast from "react-hot-toast";

/**
 * How long an undo toast stays open. Deferred writes (task delete, recurring
 * reschedule, email archive) share this window — committing after it closes
 * and being cancellable via undo/pagehide until then.
 */
export const UNDO_WINDOW_MS = 3000;

export type UndoEntry = {
  undo: () => void | Promise<void>;
  toastId: string;
};

/**
 * A pending write that has been optimistically applied to local state but
 * whose server-side commit is deferred (so the user can undo it). On
 * `pagehide`, we clear the timer and call `flush` synchronously — `flush`
 * is expected to use `fetch({ keepalive: true })` so the request can
 * outlive the page.
 */
export type PendingWrite = {
  timer: ReturnType<typeof setTimeout>;
  flush: () => void;
};

const stack: UndoEntry[] = [];
const pendingWrites = new Map<string, PendingWrite>();
let keydownAttached = false;
let pageHideAttached = false;

function handleKeyDown(e: KeyboardEvent): void {
  const target = e.target as HTMLElement | null;
  if (
    target &&
    (target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.isContentEditable)
  ) {
    return;
  }
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  if ((e.key === "u" || e.key === "U") && stack.length > 0) {
    e.preventDefault();
    const entry = stack.pop();
    if (entry) {
      void entry.undo();
      toast.dismiss(entry.toastId);
    }
  }
}

function attachKeydownOnce(): void {
  if (keydownAttached || typeof window === "undefined") return;
  keydownAttached = true;
  window.addEventListener("keydown", handleKeyDown);
}

function handlePageHide(e: PageTransitionEvent): void {
  // Skip bfcache transitions — the JS runtime survives, our timers will
  // fire normally when the page resumes. This is what makes mobile
  // tab-switching (which fires pagehide with persisted=true) safe.
  if (e.persisted) return;
  for (const [key, { timer, flush }] of pendingWrites) {
    clearTimeout(timer);
    pendingWrites.delete(key);
    flush();
  }
}

function attachPageHideOnce(): void {
  if (pageHideAttached || typeof window === "undefined") return;
  pageHideAttached = true;
  window.addEventListener("pagehide", handlePageHide);
}

export function pushUndo(entry: UndoEntry): void {
  attachKeydownOnce();
  stack.push(entry);
}

export function removeUndo(entry: UndoEntry): void {
  const i = stack.indexOf(entry);
  if (i >= 0) stack.splice(i, 1);
}

/**
 * Register a deferred server-side write so it gets flushed (with keepalive)
 * if the page is unloaded before its timer naturally fires. Callers must
 * call {@link clearPendingWrite} when the timer fires normally or when the
 * write is cancelled (e.g. via undo) to avoid the registry growing
 * unboundedly.
 */
export function registerPendingWrite(key: string, write: PendingWrite): void {
  attachPageHideOnce();
  pendingWrites.set(key, write);
}

export function clearPendingWrite(key: string): void {
  pendingWrites.delete(key);
}

/**
 * A task name as it appears inside a toast: plain text, or — when `onClick`
 * is given — an inline link that also dismisses the toast it's part of.
 * `prefix`/`suffix` surround it as plain text, so only the name itself
 * underlines (e.g. "Converted to task **"X"**" vs. "**"X"** completed").
 *
 * A plain function rather than a component — this file's other exports are
 * all non-component, and a locally-defined component would trip the
 * fast-refresh lint rule that guards against mixing the two.
 */
export function renderToastLabel({
  prefix,
  label,
  suffix,
  onClick,
  toastId,
}: {
  prefix?: string;
  label: string;
  suffix?: string;
  onClick?: () => void;
  toastId: string;
}) {
  if (!onClick) {
    return (
      <span>
        {prefix}
        {label}
        {suffix}
      </span>
    );
  }
  return (
    <span>
      {prefix}
      <button
        type="button"
        onClick={() => {
          onClick();
          toast.dismiss(toastId);
        }}
        className="text-left underline underline-offset-2 decoration-dotted hover:decoration-solid hover:text-warm-blue transition-colors"
      >
        {label}
      </button>
      {suffix}
    </span>
  );
}

/**
 * Show a toast with an Undo button that fires `onUndo`. The toast auto-dismisses
 * after `durationMs` (default 5s), and the entry is removed from the global
 * undo stack when the toast expires or the Undo button is clicked.
 *
 * When `onLabelClick` is provided, `label` is rendered as an inline link —
 * task-completion toasts use this to let the user reopen the task they just
 * completed without losing the undo affordance. `prefix`/`labelSuffix`, if
 * provided, surround the link as plain text (see {@link renderToastLabel}).
 *
 * Returns the entry so callers can remove it early if needed.
 */
export function showUndoToast(opts: {
  prefix?: string;
  label: string;
  labelSuffix?: string;
  onUndo: () => void | Promise<void>;
  onLabelClick?: () => void;
  durationMs?: number;
  toastId?: string;
}): UndoEntry {
  const durationMs = opts.durationMs ?? UNDO_WINDOW_MS;
  const toastId = opts.toastId ?? `undo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  let fired = false;

  const entry: UndoEntry = {
    undo: () => {
      if (fired) return;
      fired = true;
      removeUndo(entry);
      return opts.onUndo();
    },
    toastId,
  };

  pushUndo(entry);

  toast(
    (t) => (
      <span className="flex items-center gap-3 text-sm">
        {renderToastLabel({
          prefix: opts.prefix,
          label: opts.label,
          suffix: opts.labelSuffix,
          onClick: opts.onLabelClick,
          toastId: t.id,
        })}
        <button
          type="button"
          onClick={() => {
            void entry.undo();
            toast.dismiss(t.id);
          }}
          className="font-semibold underline underline-offset-2 whitespace-nowrap"
        >
          Undo
        </button>
        <span className="opacity-50 text-xs whitespace-nowrap">
          press <kbd className="font-semibold">U</kbd>
        </span>
      </span>
    ),
    { id: toastId, duration: durationMs },
  );

  setTimeout(() => removeUndo(entry), durationMs);

  return entry;
}

/**
 * Show a plain confirmation (or error) toast whose task name is a clickable
 * link to that task — the {@link renderToastLabel} link affordance without
 * the Undo button or keyboard shortcut that {@link showUndoToast} always
 * adds. Covers outcomes with nothing to undo: a task created, converted, or
 * moved (`type: "success"`, the default), or an operation on an existing
 * task that failed (`type: "error"`) — the task itself is untouched by the
 * failure, so it's still valid to link to.
 */
export function showTaskLinkToast(opts: {
  prefix?: string;
  label: string;
  labelSuffix?: string;
  onLabelClick: () => void;
  type?: "success" | "error";
  durationMs?: number;
  toastId?: string;
}): void {
  const toastId =
    opts.toastId ?? `task-link-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  toast[opts.type ?? "success"](
    (t) =>
      renderToastLabel({
        prefix: opts.prefix,
        label: opts.label,
        suffix: opts.labelSuffix,
        onClick: opts.onLabelClick,
        toastId: t.id,
      }),
    { id: toastId, ...(opts.durationMs !== undefined ? { duration: opts.durationMs } : {}) },
  );
}

